import React from 'react';
import { StepsConsumer, IStep } from '@alinta-digital/asg';
import { ConnectionSection } from '../ConnectionSection';
import {
  IdentificationSection,
  IdentificationSectionCore,
} from '../IdentificationSection';
import { DebitDetailsSection } from '../DebitDetailsSection';
import { ReviewSection } from '../ReviewSection';
import { ThankYou } from '../ThankYou';
import { Formik, FormikProps } from 'formik';
import { FormContainerConsumer } from '../../contexts/FormContainerContext';
import { StateType } from '../../types';
import {
  getQueryParams,
  resetFormContextError,
  deepCopy,
} from '../../utils/utils';
import { IdentificationParentForm } from '../../components/Identification/IdentificationSubForm';
import { ConnectionParentForm } from '../../components/Connection/ConnectionSubForm';
import { DebitDetailsParentForm } from '../../components/DebitDetails/DebitDetailsSubForm';
import { ReviewParentForm } from '../../components/Review/ReviewSubForm';
import FlaggedFeature from '../../utils/FlaggedFeature';
import { config } from '../../config';
import { pushAnalytics } from '../../utils/utils';

export interface DirectDebitForm
  extends IdentificationParentForm,
    ConnectionParentForm,
    DebitDetailsParentForm,
    ReviewParentForm {}

const queryParams = getQueryParams(window.location.search);
export const Form: React.FC = () => {
  React.useEffect(() => {
    pushAnalytics('load.0.dd.s1');
  }, []);
  return (
    <FormContainerConsumer
      initialValue={{
        stateShortName: queryParams.state
          ? (queryParams.state.toUpperCase() as StateType)
          : ('VIC' as StateType),
        customer: {
          customerNumber: '',
          authenticationToken: '',
          fuelSites: [],
        },
        bankBranch: {
          valid: false,
          bsbNumber: '',
          displayName: '',
          hideDisplayName: false,
        },
        error: {
          steps: [
            {
              status: false,
              type: undefined,
            },
            {
              status: false,
              type: undefined,
            },
            {
              status: false,
              type: undefined,
            },
            {
              status: false,
              type: undefined,
            },
          ],
        },
      }}
    >
      {({ values: formContextValues, dispatch: formContextDispatch }) => {
        return (
          <StepsConsumer stepCount={4}>
            {({ steps, dispatch: stepsDispatch }) => {
              const setStepFlow = (targetStep: IStep) => {
                stepsDispatch({
                  type: 'SET_STEPS',
                  value: {
                    ...steps,
                    ...targetStep,
                  },
                });
              };

              const onEditClick = (stepToEdit: string, formSteps: IStep) => {
                const stepsCopy = deepCopy(formSteps);
                const stepKeys = Object.keys(stepsCopy);
                let targetStepModified = false;

                for (const key of stepKeys) {
                  if (targetStepModified) {
                    stepsCopy[key].isComplete = false;
                  }

                  if (key === stepToEdit) {
                    stepsCopy[key].isEdit = true;
                    targetStepModified = true;
                  } else {
                    stepsCopy[key].isEdit = false;
                  }
                }

                stepsDispatch({
                  type: 'SET_STEPS',
                  value: { ...stepsCopy },
                });
              };

              return (
                <Formik<DirectDebitForm>
                  initialValues={{
                    identifyYourAccountForm: {
                      accountNumber: '',
                      firstName: '',
                      lastName: '',
                      dob: '',
                    },
                    connection: {
                      electricity: [],
                      gas: [],
                    },
                    debitDetails: {
                      paymentMethod: '',
                      accountConfirmation: false,
                      collectDataConfirmation: false,
                      invoiceConfirmation: false,
                      bankAccountName: '',
                      bsbNumber: '',
                      bankAccountNumber: '',
                    },
                    review: {
                      consent: false,
                    },
                  }}
                  onSubmit={() => undefined}
                >
                  {(formikProps: FormikProps<DirectDebitForm>) => (
                    <>
                      {!steps.step4.isComplete ? (
                        <form>
                          <FlaggedFeature
                            featureFlag={config.enableCoreIntegration}
                            fallback={
                              <IdentificationSection
                                isEdit={steps.step1.isEdit}
                                isComplete={steps.step1.isComplete}
                                formikProps={formikProps}
                                onNextClick={() => {
                                  setStepFlow({
                                    step1: { isComplete: true, isEdit: false },
                                    step2: { isComplete: false, isEdit: true },
                                  });
                                }}
                                onEditClick={() => {
                                  resetFormContextError(
                                    formContextValues,
                                    formContextDispatch,
                                    1
                                  );
                                  onEditClick('step1', steps);
                                }}
                              />
                            }
                          >
                            <IdentificationSectionCore
                              isEdit={steps.step1.isEdit}
                              isComplete={steps.step1.isComplete}
                              formikProps={formikProps}
                              onNextClick={() => {
                                setStepFlow({
                                  step1: { isComplete: true, isEdit: false },
                                  step2: { isComplete: false, isEdit: true },
                                });
                              }}
                              onEditClick={() => {
                                resetFormContextError(
                                  formContextValues,
                                  formContextDispatch,
                                  1
                                );
                                onEditClick('step1', steps);
                              }}
                            />
                          </FlaggedFeature>

                          <ConnectionSection
                            isEdit={steps.step2.isEdit}
                            isComplete={steps.step2.isComplete}
                            formikProps={formikProps}
                            onPreviousClick={() =>
                              setStepFlow({
                                step2: { isComplete: false, isEdit: false },
                                step1: { isComplete: false, isEdit: true },
                              })
                            }
                            onNextClick={() =>
                              setStepFlow({
                                step2: { isComplete: true, isEdit: false },
                                step3: { isComplete: false, isEdit: true },
                              })
                            }
                            onEditClick={() => {
                              onEditClick('step2', steps);
                            }}
                          />
                          <DebitDetailsSection
                            isEdit={steps.step3.isEdit}
                            isComplete={steps.step3.isComplete}
                            formikProps={formikProps}
                            onPreviousClick={() =>
                              setStepFlow({
                                step3: { isComplete: false, isEdit: false },
                                step2: { isComplete: false, isEdit: true },
                              })
                            }
                            onNextClick={() =>
                              setStepFlow({
                                step3: { isComplete: true, isEdit: false },
                                step4: { isComplete: false, isEdit: true },
                              })
                            }
                            onEditClick={() => onEditClick('step3', steps)}
                          />
                          <ReviewSection
                            isEdit={steps.step4.isEdit}
                            formikProps={formikProps}
                            onPreviousClick={() =>
                              setStepFlow({
                                step4: { isComplete: false, isEdit: false },
                                step3: { isComplete: false, isEdit: true },
                              })
                            }
                            onNextClick={() =>
                              setStepFlow({
                                step3: { isComplete: true, isEdit: false },
                                step4: { isComplete: true, isEdit: false },
                              })
                            }
                          />
                        </form>
                      ) : (
                        <ThankYou formikProps={formikProps} />
                      )}
                    </>
                  )}
                </Formik>
              );
            }}
          </StepsConsumer>
        );
      }}
    </FormContainerConsumer>
  );
};
